import { useRouter } from 'expo-router';
import * as StoreReview from 'expo-store-review';
import * as WebBrowser from 'expo-web-browser';
import React, { useEffect } from 'react';
import { Alert, Platform, Pressable, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import ContactSupport from '@/components/contact-support';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import mixpanelService from '@/core/clients/mixpanel';
import useSubscriptionStore from '@/stores/use-subscription-store';
import { Card, Text } from '@/ui';

export default function ThankYou() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const reset = useSubscriptionStore((state) => state.reset);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Subscription Thank You',
    });
  }, []);

  useEffect(() => {
    const promptReview = async () => {
      if (await StoreReview.hasAction()) {
        Alert.alert('Enjoying Eon?', '', [
          {
            text: "❤️ Yes, it's great",
            onPress: () => StoreReview.requestReview(),
          },
          {
            text: 'Maybe later',
            onPress: () => console.log('Cancel Pressed'),
          },
        ]);
      }
    };

    const timeoutId = setTimeout(() => {
      promptReview();
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollViewContentContainer}
        style={[
          styles.scrollViewContainer,
          { paddingTop: UnistylesRuntime.insets.top },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            flexGrow: 1,
            gap: 50,
            padding: 16,
          }}
        >
          <View>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.navigate('/subscriptions');
              }}
            />
          </View>

          <View style={{ gap: 32 }}>
            <Text tx="subscriptions.thankYouTitle" type="largeTitle" />

            <Text tx="subscriptions.thankYouBody" type="bodyRegular" />
          </View>

          <Card style={{ gap: 16, padding: 16 }}>
            <View style={{ gap: 16 }}>
              <Text tx="subscriptions.thankYouNext" type="title2" />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>
                  <Text
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                    type="bodyRegular"
                    tx="subscriptions.thankYouVerifyInfo"
                  />
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>
                  <Text
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                    type="bodyRegular"
                    tx="subscriptions.thankYouLink"
                  />
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>
                  <Text
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                    type="bodyRegular"
                    tx="subscriptions.thankYouFollowUp"
                  />
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>
                  <Text
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                    type="bodyRegular"
                    tx="subscriptions.thankYouFinalize"
                  />
                </View>
              </View>
            </View>

            <View style={{ gap: 16 }}>
              <Text tx="subscriptions.thankYouMeantime" type="title2" />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>

                  <Pressable
                    onPress={async () => {
                      const baseUrl =
                        'https://www.eonrides.com/legal/subscription-faq';

                      if (Platform.OS === 'web') {
                        window.open(baseUrl, '_blank');
                      } else {
                        await WebBrowser.openBrowserAsync(baseUrl);
                      }
                    }}
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 4,
                      flexShrink: 1,
                    }}
                  >
                    <Text>
                      <Text
                        style={{
                          color: theme.colors.text.secondary,
                        }}
                        type="bodyRegular"
                      >
                        Take a look at our{' '}
                      </Text>

                      <Text
                        style={{
                          color: theme.colors.text.secondary,
                          textDecorationLine: 'underline',
                        }}
                        type="bodyRegular"
                      >
                        FAQs
                      </Text>

                      <Text
                        style={{
                          color: theme.colors.text.secondary,
                        }}
                        type="bodyRegular"
                      >
                        {' '}
                        to get familiar with your car and your subscription
                        experience.
                      </Text>
                    </Text>
                  </Pressable>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}
                >
                  <Text
                    style={{
                      marginRight: 8,
                      color: theme.colors.text.secondary,
                    }}
                    type="bodyRegular"
                  >
                    •
                  </Text>
                  <Text
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                    type="bodyRegular"
                    tx="subscriptions.thankYouWebsite"
                  />
                </View>
              </View>
            </View>
          </Card>

          <ContactSupport />

          <View style={{ height: 50 }} />
        </View>
      </ScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollViewContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  scrollViewContentContainer: {
    marginTop: 20,
    maxWidth: {
      xs: '100%',
      md: 700,
    },
  },
}));
